import { useQuery } from '@tanstack/react-query';
import { useStore } from '@tanstack/react-store';

import { useOrganization } from '../contexts/organization-context';
import { analyticsApi } from '../lib/api-client';
import { queryKeys, STALE_TIMES } from '../lib/query-keys';
import { mergeYearOptions, setYear, yearStore } from '../stores/year-store';

/**
 * The globally selected year. Every screen reads this — the control lives once,
 * in the header, so a dashboard, an analytics tab and a comp set can never
 * disagree about which year is on screen.
 */
export function useYear(): number {
  return useStore(yearStore, (s) => s.year);
}

export function useYearSelection(): { year: number; setYear: (year: number) => void } {
  return { year: useYear(), setYear };
}

/**
 * Years the dropdown offers. Renders immediately with the current year, then
 * widens once the organisation's years arrive — bookings *or* prices, since a
 * season is priced long before it is booked and that is when goals are set.
 * Production has no data before 2025, so a fixed range would offer years that
 * render nothing. The request never blocks the header.
 */
export function useYearOptions(): number[] {
  const { organization } = useOrganization();
  const orgPid = organization?.pid ?? '';
  const year = useYear();

  const { data } = useQuery({
    queryKey: queryKeys.analytics.availableYears(orgPid),
    queryFn: () => analyticsApi.availableYears(),
    enabled: !!orgPid,
    staleTime: STALE_TIMES.LONG,
  });

  return mergeYearOptions(new Date().getFullYear(), year, data);
}
